import * as React from "react";
import {
  IProperty,
  ISharedAppContext,
  ISharedAppContextV2,
  SharedAppConsumer,
  SharedAppContextV2Consumer,
  SharedAppContextV2Provider,
  SharedAppProvider
} from "@origin-digital/shared-app-context";
import { LiveChat } from "../LiveChat";
import { Header } from "../Header";
import { IMenuItem } from "../Navigation/IMenuItem";
import { isLocalEnvironment } from "../../auth/jwtUtils";
import { NavigationHeader } from "../NavigationHeader";
import { hasScopedJwt, auth0EnvConfigs } from "@origin-digital/origin-auth";
import { initFeatures, getOriginCustomerId } from "./sacToggleHelpers";
import { CrisisBanner } from "../Banner";
import { ICustomerCodeV2 } from "@origin-digital/shared-app-context/dist/typings/generated/api";

interface ISacVersionToggleProps {
  initSAC: boolean;
  liveChat?: Window["oetal"]["pageData"]["liveChat"];
  showHeader: Window["oetal"]["pageData"]["showHeader"];
  authenticated: boolean;
  nav: IMenuItem[];
  actionBar: any;
  title: string;
}

interface ISacVersionToggleState {
  isSacV2User: boolean;
  originCustomerId?: string;
}

export class SacVersionToggle extends React.Component<
  ISacVersionToggleProps,
  ISacVersionToggleState
> {
  constructor(props) {
    super(props);
    this.state = {
      isSacV2User: this.hasScopedJwt()
    };
  }

  private hasScopedJwt = (): boolean => {
    return (
      typeof window !== "undefined" &&
      this.props.authenticated &&
      hasScopedJwt(auth0EnvConfigs[window.oetal.env.name])
    );
  };

  private useSacV2IfToggledCustomer = async () => {
    if (typeof window !== "undefined" && !isLocalEnvironment()) {
      let originCustomerId = await getOriginCustomerId();
      if (!originCustomerId) {
        // dont set up features if no origin customer id
        return;
      }
      this.setState({
        originCustomerId
      });
    }
    initFeatures(this.state.originCustomerId, featureEnabled => {
      const isSacV2User = featureEnabled;
      this.setState({ isSacV2User });
    });
  };

  async componentDidMount() {
    // sac v2 and accompanying feature switch is only attempted for authenticated apps
    if (this.props.authenticated && !this.state.isSacV2User) {
      await this.useSacV2IfToggledCustomer();
    }
  }

  render() {
    const { isSacV2User } = this.state;
    const {
      initSAC,
      liveChat,
      showHeader,
      authenticated,
      nav,
      actionBar,
      title
    } = this.props;

    return (
      <>
        {!isSacV2User && (
          <SharedAppProvider
            xhrHost={__DEV__ ? "http://localhost:4488" : undefined}
            xhrInitialize={initSAC}
          >
            <SharedAppConsumer>
              {(context: ISharedAppContext) => {
                const { customerCode, premiseId, properties, isNative } =
                  context || {};
                return (
                  <AppContent
                    actionBar={actionBar}
                    authenticated={authenticated}
                    customerCode={customerCode}
                    isNative={isNative}
                    liveChat={liveChat}
                    onSelectPremise={premiseId => {
                      context.actions.setPremiseId(premiseId);
                    }}
                    nav={nav}
                    showHeader={showHeader}
                    premiseId={premiseId}
                    properties={properties}
                    title={title}
                  />
                );
              }}
            </SharedAppConsumer>
          </SharedAppProvider>
        )}
        {isSacV2User && (
          <SharedAppContextV2Provider
            xhrHost={__DEV__ ? "http://localhost:4488" : undefined}
            xhrInitialize={initSAC}
          >
            <SharedAppContextV2Consumer>
              {(context: ISharedAppContextV2) => {
                const { customerCode, premiseId, properties, isNative } =
                  context || {};
                return (
                  <AppContent
                    actionBar={actionBar}
                    authenticated={authenticated}
                    customerCode={customerCode}
                    isNative={isNative}
                    liveChat={liveChat}
                    onSelectPremise={premiseId => {
                      context.actions.setPremiseId(premiseId);
                    }}
                    nav={nav}
                    showHeader={showHeader}
                    premiseId={premiseId}
                    properties={properties}
                    title={title}
                  />
                );
              }}
            </SharedAppContextV2Consumer>
          </SharedAppContextV2Provider>
        )}
      </>
    );
  }
}

export const AppContent: React.FC<{
  isNative: boolean;
  customerCode?: ICustomerCodeV2;
  liveChat?: Window["oetal"]["pageData"]["liveChat"];
  showHeader: Window["oetal"]["pageData"]["showHeader"];
  authenticated: boolean;
  nav: IMenuItem[];
  actionBar: any;
  title: string;
  premiseId?: string;
  properties?: IProperty[];
  onSelectPremise: (premiseId: string) => void;
}> = ({
  isNative,
  premiseId,
  properties,
  customerCode,
  liveChat,
  showHeader,
  authenticated,
  nav,
  actionBar,
  title,
  onSelectPremise
}) => (
  <>
    {isNative ? null : <CrisisBanner />}
    <LiveChat customerCode={customerCode} liveChat={liveChat} />
    <NavigationHeader
      showHeader={showHeader}
      authenticated={authenticated}
      customerCode={customerCode}
      nav={nav}
    />
    <Header
      title={actionBar.title || title}
      component={actionBar.component}
      hideActionBar={actionBar.hidden}
      mobilePropertySelector={actionBar.mobilePropertySelector}
      mobileNavButton={actionBar.mobileNavButton}
      authenticated={authenticated}
      onSelectPremise={onSelectPremise}
      premiseId={premiseId}
      premises={properties}
      isNative={isNative}
    />
  </>
);
